import React from "react";
import CheckMarkGrey from "../../../../../../images/membership/circle-check-mark-grey.svg";
import RefreshBlue from "../../../../../../images/membership/term.svg";
import { inject, observer } from "mobx-react";
import {
  DEFAULT_MONTHLY_DONATION_MESSAGE,
  DonationPricingPageInputsModel,
} from "../../../shared/models";
import {
  CollapsiblePanel,
  CoreInputModelUtil,
  CustomLabel,
  DraftEditor,
  FlexContainer,
  IconButton,
  InfoIcon,
  Input,
  removeComma,
  SingleSelect,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { DonationStore } from "../../../shared/store";
import { DonationPricingOption, DonationType } from "../../../shared/enums";
import { PricingOption } from "../../../shared/components";
import { DescriptionCell, LabelCell } from "../../../shared/styles";
import { isValidAmountKeystroke } from "../../../shared/utils/AmountInputUtil";
import { convertToRaw, EditorState } from "draft-js";
type IProps = {
  donationStore?: DonationStore;
};

type IDonationType = {
  icon: string;
  text: string;
  key: keyof DonationType;
};

@inject("donationStore")
@observer
class DonationPriceSection extends React.Component<IProps> {
  get donationStore() {
    return this.props.donationStore;
  }

  updateState(
    key: keyof DonationPricingPageInputsModel,
    value: string | string[] | number | EditorState,
  ) {
    if (!this.donationStore) return;
    this.props.donationStore?.updateScrollToField(key);
    switch (key) {
      case "pricingOption":
        this.props.donationStore?.updatePricingPageData(
          new DonationPricingPageInputsModel({
            ...this.donationStore.pricingPageData,
            pricingOption: value as keyof DonationPricingOption,
            customAmount: "",
          }),
        );
        break;
      case "defaultPrice":
        value = removeComma(value as string);

        //ON DOUBLE CLICK REMOVE DEFAULT PRICE
        if (value === this.donationStore.pricingPageData.defaultPrice) {
          value = "";
        }
        this.props.donationStore?.updatePricingPageData(
          new DonationPricingPageInputsModel({
            ...this.donationStore.pricingPageData,
            defaultPrice: value,
            donationAmout: parseFloat(value),
          }),
        );
        break;
      case "message":
        this.props.donationStore?.updatePricingPageData(
          new DonationPricingPageInputsModel({
            ...this.donationStore.pricingPageData,
            message: value as string,
          }),
        );
        break;
      case "messageEditorState":
        const editorState = value as EditorState;
        this.props.donationStore?.updatePricingPageData(
          new DonationPricingPageInputsModel({
            ...this.donationStore.pricingPageData,
            messageEditorState: editorState,
            message: editorState.getCurrentContent().getPlainText(),
            messageEditorRawContent: JSON.stringify(
              convertToRaw(editorState.getCurrentContent()),
            ),
          }),
        );
        break;
      case "description":
        this.props.donationStore?.updatePricingPageData(
          new DonationPricingPageInputsModel({
            ...this.donationStore.pricingPageData,
            description: value as string,
          }),
        );
        break;
      case "descriptionEditorState":
        const typeEditorState = value as EditorState;
        this.props.donationStore?.updatePricingPageData(
          new DonationPricingPageInputsModel({
            ...this.donationStore.pricingPageData,
            descriptionEditorState: typeEditorState,
            description: typeEditorState.getCurrentContent().getPlainText(),
            descriptionEditorRawContent: JSON.stringify(
              convertToRaw(typeEditorState.getCurrentContent()),
            ),
          }),
        );
        break;
      case "type": {
        const isOneTimeOrMonthly =
          DonationType[value as keyof typeof DonationType] ===
          DonationType.OneTimeOrMonthly;
        const hasDescription =
          !!this.donationStore.pricingPageData.description?.trim();
        // When "One Time or Monthly" is selected, surface the default message
        // immediately (no need to click into the field) if none is configured.
        const seedDefault = isOneTimeOrMonthly && !hasDescription;
        const defaultEditorState = seedDefault
          ? UtilityMethods.getEditorStateFromPlainText(
              DEFAULT_MONTHLY_DONATION_MESSAGE,
            )
          : undefined;
        this.props.donationStore?.updatePricingPageData(
          new DonationPricingPageInputsModel({
            ...this.donationStore.pricingPageData,
            type: value as keyof DonationType,
            ...(seedDefault && defaultEditorState
              ? {
                  description: DEFAULT_MONTHLY_DONATION_MESSAGE,
                  descriptionEditorState: defaultEditorState,
                  descriptionEditorRawContent: JSON.stringify(
                    convertToRaw(defaultEditorState.getCurrentContent()),
                  ),
                }
              : {}),
          }),
        );
        break;
      }
      case "customAmount":
        if (!isValidAmountKeystroke(value as string)) {
          return;
        }
        this.props.donationStore?.updatePricingPageData(
          new DonationPricingPageInputsModel({
            ...this.donationStore.pricingPageData,
            customAmount: value as string,
            defaultPrice: value as string,
          }),
        );
        break;
      case "customAmounts":
        this.props.donationStore?.updatePricingPageData(
          new DonationPricingPageInputsModel({
            ...this.donationStore.pricingPageData,
            customAmounts: value as string[],
          }),
        );
        break;
      case "selectedCustomIndex":
        this.props.donationStore?.updatePricingPageData(
          new DonationPricingPageInputsModel({
            ...this.donationStore.pricingPageData,
            selectedCustomIndex: value as number,
          }),
        );
        break;
    }
  }

  get options() {
    return Object.keys(DonationPricingOption).map((key: string) => {
      return {
        value: key,
        label: DonationPricingOption[key as keyof typeof DonationPricingOption],
      };
    });
  }

  updateCustomAmount(key: string, value: string) {
    if (!this.donationStore) return;
    const numericValue = parseFloat(value);
    if (
      !isValidAmountKeystroke(value as string) ||
      numericValue < 1 ||
      numericValue > 99999
    ) {
      return;
    }
    const index = parseInt(key.split("-")[1], 10);
    const customAmounts = [...this.donationStore.pricingPageData.customAmounts];
    customAmounts[index] = value;
    this.updateState("customAmounts", customAmounts);
  }

  get donationPrices() {
    if (!this.donationStore) {
      return null;
    }

    const pricingOption =
      DonationPricingOption[
        this.donationStore.pricingPageData
          .pricingOption as keyof typeof DonationPricingOption
      ];
    const isAllCustom = pricingOption === DonationPricingOption.CustomAll;

    if (isAllCustom) {
      const customAmounts = Array(6)
        .fill(0)
        .map((_, index) => ({
          key: `custom-${index}`,
          value:
            this.donationStore?.pricingPageData.customAmounts?.[index] || "",
        }));
      return (
        <FlexContainer
          spacing={15}
          padding="25px 0px 15px"
          fxWrap="wrap"
          className="pricing-option-wrapper"
        >
          {customAmounts.map((custom, index) => {
            const isSelected =
              this.donationStore?.pricingPageData.defaultPrice ===
                custom.value &&
              custom.value !== "" &&
              this.donationStore?.pricingPageData.selectedCustomIndex === index;
            return (
              <PricingOption
                key={custom.key}
                value={custom.value}
                showInput={true}
                isSelected={isSelected}
                onChange={(value: string) =>
                  this.updateCustomAmount(custom.key, value)
                }
                onClick={() => {
                  this.updateState("selectedCustomIndex", index);
                  this.updateState(
                    "defaultPrice",
                    this.donationStore?.pricingPageData.customAmounts[index] ||
                      "",
                  );
                }}
                showError={this.donationStore?.pricingPageData.hasError(
                  `customAmounts${index}` as keyof DonationPricingPageInputsModel,
                )}
              />
            );
          })}
          {this.donationStore?.pricingPageData.hasError("customAmounts") && (
            <p className="error-message">
              {this.donationStore?.pricingPageData.getErrorMessage(
                "customAmounts",
              )}
            </p>
          )}
        </FlexContainer>
      );
    }

    if (
      [
        DonationPricingOption.Option1,
        DonationPricingOption.Option2,
        DonationPricingOption.Option3,
        DonationPricingOption.Option4,
        DonationPricingOption.Option5,
      ].includes(pricingOption)
    ) {
      return (
        <FlexContainer
          spacing={15}
          padding="25px 0px 15px"
          fxWrap="wrap"
          className="pricing-option-wrapper"
        >
          {pricingOption.split(", ").map((price) => {
            let isSelected = false;
            if (price.trim() === "Custom") {
              const customAmount =
                this.donationStore?.pricingPageData.customAmount || "";
              isSelected =
                customAmount !== "" &&
                this.donationStore?.pricingPageData.defaultPrice ===
                  customAmount;
              return (
                <PricingOption
                  key={price}
                  value={customAmount}
                  isSelected={isSelected}
                  showInput={true}
                  onChange={(value: string) =>
                    this.updateState("customAmount", value)
                  }
                  onClick={() => this.updateState("defaultPrice", customAmount)}
                />
              );
            }

            const parsedPrice = parseFloat(
              UtilityMethods.removeSpecialCharacters(price),
            );
            isSelected =
              parsedPrice.toString() ===
              this.donationStore?.pricingPageData.defaultPrice;
            return (
              <PricingOption
                key={price}
                value={price}
                isSelected={isSelected}
                onClick={() =>
                  this.updateState("defaultPrice", parsedPrice.toString())
                }
              />
            );
          })}
        </FlexContainer>
      );
    }

    return null;
  }

  get donationTypes(): IDonationType[] {
    return [
      {
        icon: RefreshBlue,
        text: "One Time Only",
        key: Object.keys(DonationType).find(
          (eKey) =>
            DonationType[eKey as keyof typeof DonationType] ===
            DonationType.OneTime,
        ) as keyof DonationType,
      } as IDonationType,
      {
        icon: RefreshBlue,
        text: "One Time or Monthly",
        key: Object.keys(DonationType).find(
          (eKey) =>
            DonationType[eKey as keyof typeof DonationType] ===
            DonationType.OneTimeOrMonthly,
        ) as keyof DonationType,
      } as IDonationType,
    ];
  }

  get donationTypeRow() {
    return (
      <FlexContainer className="label-description-row">
        <LabelCell>
          <CustomLabel lineHeight="40px" fontSize="16px">
            Donation Type
            <InfoIcon />
          </CustomLabel>
        </LabelCell>
        <DescriptionCell>
          <FlexContainer spacing={15} fxLayout="row" padding="0px 0px 30px">
            {this.donationTypes.map((donation) => {
              const className =
                donation.key === this.donationStore?.pricingPageData?.type
                  ? "active"
                  : "";
              return (
                <IconButton
                  key={donation.key.toString()}
                  gap="0px"
                  className={`action-button  ${className}`}
                  onClick={() =>
                    this.updateState("type", donation.key.toString())
                  }
                >
                  <img src={donation.icon} />
                  {donation.text}
                </IconButton>
              );
            })}
          </FlexContainer>
          {DonationType[
            this.donationStore?.pricingPageData
              ?.type as keyof typeof DonationType
          ] === DonationType.OneTimeOrMonthly && (
            <>
              <div>
                {/* <Input
                  error={this.donationStore?.pricingPageData.hasError(
                    "description"
                  )}
                  type="textarea"
                  placeholder="Help us out with a monthly donation!"
                  value={this.donationStore?.pricingPageData.description || ""}
                  onChange={(e) =>
                    this.updateState("description", e.target.value)
                  }
                /> */}
                <DescriptionCell>
                  <DraftEditor
                    editorState={
                      this.donationStore?.pricingPageData
                        .descriptionEditorState || EditorState.createEmpty()
                    }
                    onChange={(newValue: EditorState) =>
                      this.updateState("descriptionEditorState", newValue)
                    }
                    className="product-detail-editor"
                    showErrorBorder={this.donationStore?.pricingPageData.hasError(
                      "description",
                    )}
                  />
                </DescriptionCell>
              </div>
              <CustomLabel color="#979797" fontSize="14px" lineHeight="16px">
                Entice donors to make their donation recurring.
              </CustomLabel>
            </>
          )}
        </DescriptionCell>
      </FlexContainer>
    );
  }
  render() {
    return (
      <CollapsiblePanel title="Price">
        <FlexContainer className="label-description-row">
          <LabelCell>
            <CustomLabel lineHeight="40px" fontSize="16px">
              Pricing Option
              <InfoIcon />
            </CustomLabel>
          </LabelCell>
          <DescriptionCell>
            <FlexContainer alignItems="center" spacing={10}>
              <SingleSelect
                hasError={this.donationStore?.pricingPageData.hasError(
                  "pricingOption",
                )}
                selectedKey={
                  this.donationStore?.pricingPageData?.pricingOption?.toString() ||
                  ""
                }
                nameKeys={["label"]}
                valueKeys={["value"]}
                options={[this.options]}
                onChange={(option) =>
                  this.updateState("pricingOption", option.value)
                }
                className="donation-amount dpdn-no-separator"
              />
              <CustomLabel>USD</CustomLabel>
            </FlexContainer>
            {this.donationPrices}
            <CustomLabel color="#979797" fontSize="14px" lineHeight="16px">
              <img src={CheckMarkGrey} className="placeholder-check" />
              Click to select a default price. Price must be entered if custom
              is the default.
            </CustomLabel>
          </DescriptionCell>
        </FlexContainer>
        <FlexContainer className="label-description-row">
          <LabelCell>
            <CustomLabel lineHeight="40px" fontSize="16px">
              Message
              <InfoIcon />
            </CustomLabel>
          </LabelCell>
          <DescriptionCell className="donation-textarea-plh">
            {/* <Input
              type="textarea"
              error={this.donationStore?.pricingPageData.hasError("message")}
              value={this.donationStore?.pricingPageData.message || ""}
              onChange={(e) => this.updateState("message", e.target.value)}
              placeholder="Make a donation of $1,000 and receive something special!"
            /> */}
            <DescriptionCell>
              <DraftEditor
                editorState={
                  this.donationStore?.pricingPageData.messageEditorState ||
                  EditorState.createEmpty()
                }
                onChange={(newValue: EditorState) =>
                  this.updateState("messageEditorState", newValue)
                }
                className="product-detail-editor"
              />
            </DescriptionCell>
            <CustomLabel color="#979797" fontSize="14px" lineHeight="16px">
              Include something special at specific amounts. Set up in options.
            </CustomLabel>
          </DescriptionCell>
        </FlexContainer>
        {this.donationTypeRow}
      </CollapsiblePanel>
    );
  }
}

export default DonationPriceSection;
